import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { assets } from '../../../core/constants/assets.const';
import { ActivatedRoute, Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { VirtualScrollService } from '../../../core/services/virtual-scroll.service';
import { ISearchHistory } from '../../../core/models/search-history.interface';
import { SearchStateService } from '../../../core/services/search-state.service';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'real-estate-website-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements AfterViewInit, OnDestroy {
  @ViewChild('search') searchRef!: ElementRef;

  @Input() placeholder: string = 'Search…';

  @Output() searchOutput: EventEmitter<string> = new EventEmitter();
  assetsConst = assets;

  public showCrossIcon: WritableSignal<boolean> = signal(false);
  public showDropdown: WritableSignal<boolean> = signal(false);
  dropdownData$!: Observable<ISearchHistory[]>;

  protected readonly formControl = new FormControl<string>('');

  constructor(
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly searchStateService: SearchStateService,
    private readonly virtualScrollService: VirtualScrollService,
    private readonly destroyRef: DestroyRef
  ) {
    effect(() => {
      this.formControl.setValue(this.searchStateService.searchQuery(), {
        emitEvent: false,
      });

      this.router.navigate([], {
        replaceUrl: true,
        queryParams: {
          search: this.searchStateService.searchQuery(),
        },
      });
    });
  }
  ngOnDestroy(): void {
    this.searchStateService.setSearchQuery('');
  }

  ngAfterViewInit(): void {
    this.dropdownData$ = this.authService.isGuest ? of([]) : this.searchStateService.getSearchHistory();

    this.route.queryParams
      .pipe(
        take(1),
        tap(params => {
          if (params['search']) {
            this.searchStateService.setSearchQuery(params['search']);

            this.showCrossIcon.set(true);
          }
        })
      )
      .subscribe();

    this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        tap(value => {
          this.showCrossIcon.set(!!value);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public onEnter(value: string): void {
    if (value) {
      this.showDropdown.set(false);
      this.virtualScrollService.resetPages();
      this.searchOutput.emit(value);
    }
  }

  clearInputField(): void {
    this.formControl.reset();
    this.showCrossIcon.set(false);
    this.showDropdown.set(false);
    this.searchStateService.setSearchQuery('');
    this.searchOutput.emit('');
    this.router.navigate([], {
      queryParams: { search: this.searchStateService.searchQuery() },
      replaceUrl: true,
    });
  }

  onFocus(): void {
    this.dropdownData$
      .pipe(
        take(1),
        filter(date => date.length > 0),
        tap(() => this.showDropdown.set(true))
      )
      .subscribe();
  }

  onSelectItem(searchValue: string): void {
    if (searchValue) {
      this.virtualScrollService.resetPages();
      this.searchOutput.emit(searchValue);
      this.formControl.setValue(searchValue);
      this.showDropdown.set(false);
    }
  }
}
